'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckCircleSolid, InfoCircleSolid, Trash, WarningCircleSolid, Xmark } from '@byway/icons';
import { useToast, useToastRegion } from '@react-aria/toast';
import { type ToastState, useToastState } from '@react-stately/toast';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import React, { createContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '~/components/Button';
import styles from './Toast.module.css';
import type { MyToast, ToastProps, ToastRegionProps } from './Toast.types';
const ICONS = {
  success: <CheckCircleSolid size="large" color="var(--by-color-icon-success)" decorative />,
  warning: <WarningCircleSolid size="large" color="var(--by-color-icon-error)" decorative />,
  info: <InfoCircleSolid size="large" color="var(--by-color-icon-informational)" decorative />,
  trash: <Trash size="large" color="var(--by-color-icon-error)" decorative />
};
export const ToastContext = createContext<ToastState<MyToast> | null>(null);
export function Toast(t0) {
  const $ = _c(49);
  let props;
  let state;
  if ($[0] !== t0) {
    ({
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
  } else {
    props = $[1];
    state = $[2];
  }
  const t = useTranslations("aria");
  const ref = useRef(null);
  const {
    toastProps,
    titleProps,
    closeButtonProps
  } = useToast(props, state, ref);
  const content = props.toast.content;
  let t1;
  if ($[3] !== content.width) {
    t1 = content.width ? {
      width: content.width,
      maxWidth: "100%"
    } : undefined;
    $[3] = content.width;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const widthStyle = t1;
  if (content.render) {
    let t2;
    if ($[5] !== content.shadow) {
      t2 = clsx(styles.toast, {
        [styles.shadow]: content.shadow
      });
      $[5] = content.shadow;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== content || $[8] !== props.toast.key || $[9] !== state) {
      let t4;
      if ($[11] !== props.toast.key || $[12] !== state) {
        t4 = () => state.close(props.toast.key);
        $[11] = props.toast.key;
        $[12] = state;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      t3 = content.render(t4);
      $[7] = content;
      $[8] = props.toast.key;
      $[9] = state;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[14] !== t2 || $[15] !== t3 || $[16] !== toastProps || $[17] !== widthStyle) {
      t4 = <div {...toastProps} ref={ref} className={t2} style={widthStyle}>{t3}</div>;
      $[14] = t2;
      $[15] = t3;
      $[16] = toastProps;
      $[17] = widthStyle;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  const {
    variant,
    title,
    description
  } = content;
  const t2 = variant === "success";
  const t3 = variant === "error";
  const t4 = variant === "warning";
  const t5 = variant === "info";
  const t6 = variant === "trash";
  let t7;
  if ($[19] !== content.shadow || $[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = clsx([styles.toast, {
      [styles.success]: t2,
      [styles.error]: t3,
      [styles.warning]: t4,
      [styles.info]: t5,
      [styles.trash]: t6,
      [styles.shadow]: content.shadow
    }]);
    $[19] = content.shadow;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== variant) {
    t8 = variant !== "error" && <div className={styles.icon}>{ICONS[variant]}</div>;
    $[26] = variant;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== title || $[29] !== titleProps) {
    t9 = <div {...titleProps} className={styles.title}>{title}</div>;
    $[28] = title;
    $[29] = titleProps;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== description) {
    t10 = description && <div className={styles.description}>{description}</div>;
    $[31] = description;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t9) {
    t11 = <div className={styles.content}>{t9}{t10}</div>;
    $[33] = t10;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t) {
    t12 = t("close");
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Xmark color="black" size="large" decorative={true} />;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== closeButtonProps || $[40] !== t12) {
    t14 = <Button isTransparent={true} size="small" variant="icon" {...closeButtonProps} ariaLabel={t12}>{t13}</Button>;
    $[39] = closeButtonProps;
    $[40] = t12;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t11 || $[43] !== t14 || $[44] !== t7 || $[45] !== t8 || $[46] !== toastProps || $[47] !== widthStyle) {
    t15 = <div {...toastProps} ref={ref} style={widthStyle} className={t7}>{t8}{t11}{t14}</div>;
    $[42] = t11;
    $[43] = t14;
    $[44] = t7;
    $[45] = t8;
    $[46] = toastProps;
    $[47] = widthStyle;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
}
export function ToastRegion(t0) {
  const $ = _c(14);
  let props;
  let state;
  let t1;
  if ($[0] !== t0) {
    ({
      state,
      position: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
    $[3] = t1;
  } else {
    props = $[1];
    state = $[2];
    t1 = $[3];
  }
  const position = t1 === undefined ? "bottom" : t1;
  const ref = React.useRef(null);
  const {
    regionProps
  } = useToastRegion(props, state, ref);
  const t2 = position === "bottom";
  const t3 = position === "middle";
  const t4 = position === "top";
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = clsx(styles.toastRegion, {
      [styles.toastRegionBottom]: t2,
      [styles.toastRegionMiddle]: t3,
      [styles.toastRegionTop]: t4
    });
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== state) {
    t6 = state.visibleToasts.map(toast => <Toast key={toast.key} toast={toast} state={state} />);
    $[8] = state;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== regionProps || $[11] !== t5 || $[12] !== t6) {
    t7 = <div {...regionProps} ref={ref} className={t5}>{t6}</div>;
    $[10] = regionProps;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function ToastProvider(t0) {
  const $ = _c(28);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      maxVisibleToasts: 5
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const state = useToastState(t1);
  let t2;
  if ($[4] !== state.visibleToasts) {
    t2 = state.visibleToasts.filter(_temp);
    $[4] = state.visibleToasts;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const bottomToasts = t2;
  let t3;
  if ($[6] !== state.visibleToasts) {
    t3 = state.visibleToasts.filter(_temp2);
    $[6] = state.visibleToasts;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const middleToasts = t3;
  let t4;
  if ($[8] !== state.visibleToasts) {
    t4 = state.visibleToasts.filter(_temp3);
    $[8] = state.visibleToasts;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const topToasts = t4;
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  let t5;
  if ($[10] !== props || $[11] !== state || $[12] !== topToasts) {
    t5 = portalTarget && topToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: topToasts
    }} position="top" />, portalTarget);
    $[10] = props;
    $[11] = state;
    $[12] = topToasts;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== middleToasts || $[15] !== props || $[16] !== state) {
    t6 = portalTarget && middleToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: middleToasts
    }} position="middle" />, portalTarget);
    $[14] = middleToasts;
    $[15] = props;
    $[16] = state;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== bottomToasts || $[19] !== props || $[20] !== state) {
    t7 = portalTarget && bottomToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: bottomToasts
    }} position="bottom" />, portalTarget);
    $[18] = bottomToasts;
    $[19] = props;
    $[20] = state;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== children || $[23] !== state || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <ToastContext.Provider value={state}>{children}{t5}{t6}{t7}</ToastContext.Provider>;
    $[22] = children;
    $[23] = state;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
function _temp3(toast_1) {
  return toast_1.content.position === "top";
}
function _temp2(toast_0) {
  return toast_0.content.position === "middle";
}
function _temp(toast) {
  return !toast.content.position || toast.content.position === "bottom";
}
export function useToasts() {
  const toastsState = React.useContext(ToastContext);
  if (!toastsState) {
    throw new Error("useToasts must be used within a ToastProvider");
  }
  return toastsState;
}